import { useEffect, useCallback, useRef, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Box, Grid, LinearProgress, useMediaQuery } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import type { ProjectFormData } from '../../interfaces/ui/addProjectModal';
import { ProjectsHeader, type ViewMode } from './components/ProjectsHeader';
import { IqeProjectCard } from '../ProjectCard';
import { ProjectsTable } from './components/ProjectsTable';

import { EmptyProjectsState } from './components/EmptyProjectsState';
import { RegenerateConfirmDialog } from './components/RegenerateConfirmDialog';
import { AddProjectModal } from '../AddProjectModal';
import BackButton from '../../../../../utils/backButton';
import { HelpComponent } from '../../../../../utils/helpComponent';
import HELP_URL from '../../../../../utils/helpLinkConstant';

import { useProjectsState } from '../../hooks/useProjectsState';
import { useModalState } from '../../hooks/useModalState';
import useTestcaseExecutionState from '../../hooks/useTestcaseExecutionState';
import useRegenerateState from '../../hooks/useRegenerateState';
import {
  getGenerateOperation,
  getModeFromStatus,
} from '../../utils/projectExecutionLifecycle';
import { isProjectOperationInProgress } from '../../utils/projectStatusChecks';
import { useStyles } from '../../styles';
import '../../styles/projectsList.css';

export const IqeProjectsList = ({ isToolAdmin }: { isToolAdmin: boolean }) => {
  const classes: any = useStyles();
  const isMobile = useMediaQuery('(max-width: 1023px)');
  const { projectId } = useParams();
  const {
    projects,
    loadingIqeProjects,
    saveProject,
    isSavingProject,
    getProjectById,
    refreshProjectStatus,
    refreshingProjectIds,
    rerunProjectValidation,
    rerunningProjectIds,
    performProjectOperation,
  } = useProjectsState(projectId!);
  const { openResults } = useTestcaseExecutionState();

  const {
    regenerateState,
    openRegenerateDialog,
    closeRegenerateDialog,
    confirmRegenerate,
  } = useRegenerateState();
  const { modalState, openModal, closeModal } = useModalState();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const projectsViewMode = (location.state as any)?.projectsViewMode;
    return projectsViewMode === 'list' ? 'list' : 'grid';
  });
  const handleViewModeChange = useCallback(
    (mode: ViewMode) => {
      setViewMode(mode);
      navigate('.', {
        replace: true,
        state: { ...(location.state as object), projectsViewMode: mode },
      });
    },
    [location.state, navigate],
  );
  const generatingClickedRef = useRef<Set<string>>(new Set());
  const [generatingProjectIds, setGeneratingProjectIds] = useState<Set<string>>(
    new Set(),
  );

  const navigateToExecutionCenter = useCallback(
    (
      iqeProjectId: string,
      runId?: string,
      query?: { tab?: string; mode?: string },
    ) => {
      const params = query
        ? new URLSearchParams(query as Record<string, string>)
        : null;
      const qs = params?.toString() ? `?${params}` : '';
      const path = runId
        ? `./${iqeProjectId}/runs/${runId}${qs}`
        : `./${iqeProjectId}${qs}`;
      navigate(path, { state: { projectsViewMode: viewMode } });
    },
    [navigate, viewMode],
  );

  // Handle result-opening intent when navigating back from execution pages.
  useEffect(() => {
    const openResultsProjectId = (location.state as any)?.openResultsProjectId;

    if (openResultsProjectId) {
      openResults(openResultsProjectId);
      navigate('.', {
        replace: true,
        state: {
          ...(location.state as object),
          openResultsProjectId: undefined,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, navigate, openResults]);

  const handleAddProject = useCallback(
    async (projectData: ProjectFormData) => {
      if (!isToolAdmin) return;
      await saveProject(projectData, projectId!);
      closeModal();
    },
    [closeModal, isToolAdmin, modalState.editingProjectId, projectId],
  );

  const handleRefreshStatus = useCallback(
    (iqeProjectId: string) => {
      refreshProjectStatus(projectId!, iqeProjectId);
    },
    [projects],
  );

  const handleRerunValidation = useCallback(
    (iqeProjectId: string) => {
      if (!isToolAdmin) return;
      rerunProjectValidation(iqeProjectId);
    },
    [isToolAdmin, rerunProjectValidation],
  );

  const handleGenerateTests = useCallback(
    async (iqeProjectId: string) => {
      if (!isToolAdmin) return;
      if (generatingClickedRef.current.has(iqeProjectId)) return;
      generatingClickedRef.current.add(iqeProjectId);
      setGeneratingProjectIds(prev => {
        const next = new Set(prev);
        next.add(iqeProjectId);
        return next;
      });

      const project = getProjectById(iqeProjectId);
      if (!project) {
        generatingClickedRef.current.delete(iqeProjectId);
        setGeneratingProjectIds(prev => {
          const next = new Set(prev);
          next.delete(iqeProjectId);
          return next;
        });
        return;
      }
      const operation = getGenerateOperation(
        project.status,
        project.runs?.length || 0,
      );

      try {
        const runId = await performProjectOperation(
          projectId!,
          iqeProjectId,
          operation,
        );
        if (runId) {
          navigateToExecutionCenter(iqeProjectId, runId, {
            tab: 'activity',
            mode: 'generate',
          });
        }
      } finally {
        generatingClickedRef.current.delete(iqeProjectId);
        setGeneratingProjectIds(prev => {
          const next = new Set(prev);
          next.delete(iqeProjectId);
          return next;
        });
      }
    },
    [
      getProjectById,
      isToolAdmin,
      navigateToExecutionCenter,
      performProjectOperation,
      projectId,
    ],
  );

  const handleRunTests = useCallback(
    (iqeProjectId: string) => {
      const project = getProjectById(iqeProjectId);
      const lastRunId = project?.runs?.[0]?.transactionId;
      if (lastRunId) {
        navigateToExecutionCenter(iqeProjectId, lastRunId, {
          tab: 'cases',
          mode: 'execute',
        });
      }
    },
    [getProjectById, navigateToExecutionCenter],
  );

  const handleRegenerateTests = useCallback(
    (iqeProjectId: string) => {
      openRegenerateDialog(iqeProjectId);
    },
    [openRegenerateDialog],
  );

  const handleViewResults = useCallback(
    (selectedProjectId: string) => {
      openResults(selectedProjectId);
    },
    [openResults],
  );

  const handleViewProjectDetails = useCallback(
    (iqeProjectId: string) => {
      navigate(iqeProjectId);
    },
    [navigate],
  );

  const handleCardNavigateToExecution = useCallback(
    (iqeProjectId: string) => {
      const project = getProjectById(iqeProjectId);
      if (!project) return;
      const isInProgress = isProjectOperationInProgress(project.status);
      const mode = getModeFromStatus(project.status);
      const currentRunId = project.transactionId;
      const lastRunId = project.runs?.[0]?.transactionId;
      // Always navigate to the ExecutionCenter route; if no runId, use a placeholder
      const runIdForNav = currentRunId || lastRunId || 'latest';

      if (isInProgress && runIdForNav !== 'latest') {
        const params = new URLSearchParams({ tab: 'activity', mode });
        navigate(`./${iqeProjectId}/runs/${runIdForNav}?${params}`, {
          state: { projectsViewMode: viewMode },
        });
        return;
      }

      navigate(`./${iqeProjectId}/runs/${runIdForNav}`, {
        state: { projectsViewMode: viewMode },
      });
    },
    [getProjectById, navigate, viewMode],
  );

  const handleViewCurrentStatus = useCallback(
    (
      iqeProjectId: string,
      _projectName: string,
      transactionId: string,
      mode: 'generate' | 'execute' | 'enhance',
    ) => {
      if (transactionId)
        navigateToExecutionCenter(iqeProjectId, transactionId, {
          tab: 'activity',
          mode,
        });
    },
    [navigateToExecutionCenter],
  );

  const handleOpenAddModal = useCallback(() => {
    if (!isToolAdmin) return;
    openModal();
  }, [isToolAdmin, openModal]);

  const handleCloseModal = useCallback(() => {
    closeModal();
  }, [closeModal]);

  const handleConfirmRegenerate = useCallback(() => {
    confirmRegenerate(iqeProjectId => {
      const project = getProjectById(iqeProjectId);
      if (project && project.transactionId)
        navigateToExecutionCenter(iqeProjectId, project.transactionId);
    });
  }, [confirmRegenerate, getProjectById, navigateToExecutionCenter]);

  const handleCancelRegenerate = useCallback(() => {
    closeRegenerateDialog();
  }, [closeRegenerateDialog]);

  const navBar = (
    <Grid item xs={12} sm={12} md={12} lg={12} className={classes.pageNavRow}>
      <BackButton />
      <HelpComponent helpUrl={HELP_URL.IQE_AGENT_APPLICATION_INFO_PAGE} />
    </Grid>
  );

  const filteredProjects = searchQuery.trim()
    ? projects.filter(
        p =>
          p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.appUrl.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.environment.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : projects;

  const SkeletonCard = () => (
    <Box
      className="pc-card"
      style={{
        backgroundColor: 'var(--iqe-bg-paper)',
        border: '1px solid var(--iqe-divider)',
        borderRadius: '8px',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
    >
      {/* Header */}
      <Skeleton variant="text" width="80%" height={24} />
      {/* Badges */}
      <Box style={{ display: 'flex', gap: '8px' }}>
        <Skeleton
          variant="rect"
          width={80}
          height={28}
          style={{ borderRadius: '16px' }}
        />
        <Skeleton
          variant="rect"
          width={100}
          height={28}
          style={{ borderRadius: '16px' }}
        />
      </Box>
      {/* Fields */}
      <Box
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          minHeight: '65px',
        }}
      >
        <Skeleton variant="text" width="100%" height={16} />
        <Skeleton variant="text" width="100%" height={16} />
      </Box>
      {/* Stats */}
      <Box style={{ display: 'flex', gap: '8px', minHeight: '70px' }}>
        <Skeleton
          variant="rect"
          width="100%"
          height={50}
          style={{ borderRadius: '6px' }}
        />
        <Skeleton
          variant="rect"
          width="100%"
          height={50}
          style={{ borderRadius: '6px' }}
        />
        <Skeleton
          variant="rect"
          width="100%"
          height={50}
          style={{ borderRadius: '6px' }}
        />
        <Skeleton
          variant="rect"
          width="100%"
          height={50}
          style={{ borderRadius: '6px' }}
        />
        <Skeleton
          variant="rect"
          width="100%"
          height={50}
          style={{ borderRadius: '6px' }}
        />
      </Box>
      {/* Info bar */}
      <Skeleton
        variant="rect"
        width="100%"
        height={55}
        style={{ borderRadius: '4px' }}
      />
    </Box>
  );

  if (loadingIqeProjects) {
    return (
      <>
        {navBar}
        <Box
          component="section"
          className="projects-grid-section"
          style={{ marginTop: '16px' }}
        >
          <LinearProgress />
          <Box className="projects-grid" style={{ marginTop: '16px' }}>
            {[...Array(6)].map((_, idx) => (
              <SkeletonCard key={idx} />
            ))}
          </Box>
        </Box>
      </>
    );
  }

  return (
    <>
      {navBar}
      <main className={classes.projectCard}>
        <ProjectsHeader
          isToolAdmin={isToolAdmin}
          projectCount={filteredProjects.length}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onAddProject={handleOpenAddModal}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
        />

        {!loadingIqeProjects && projects.length === 0 && (
          <EmptyProjectsState
            onAddProject={handleOpenAddModal}
            isToolAdmin={isToolAdmin}
          />
        )}
        {!loadingIqeProjects && projects.length > 0 && (
          <>
            {viewMode === 'grid' ? (
              <Box
                component="section"
                className="projects-grid-section"
                aria-labelledby="projects-grid-title"
              >
                <Box
                  className="projects-grid"
                  style={{
                    boxSizing: 'border-box',
                    justifyItems: isMobile ? 'center' : undefined,
                  }}
                >
                  {filteredProjects.map(project => (
                    <IqeProjectCard
                      key={project.id}
                      isToolAdmin={isToolAdmin}
                      project={project}
                      isGenerating={generatingProjectIds.has(
                        'iqeProjectId' in project &&
                          typeof project.iqeProjectId === 'string'
                          ? project.iqeProjectId
                          : project.id,
                      )}
                      isRefreshingStatus={refreshingProjectIds.includes(
                        project.id,
                      )}
                      onRefreshStatus={handleRefreshStatus}
                      onGenerateTests={handleGenerateTests}
                      onViewCurrentStatus={handleViewCurrentStatus}
                      onRunTests={handleRunTests}
                      onRegenerateTests={handleRegenerateTests}
                      onViewResults={handleViewResults}
                      isRerunning={rerunningProjectIds.includes(project.id)}
                      onRerunValidation={handleRerunValidation}
                      onNavigateToExecution={handleCardNavigateToExecution}
                      onViewProjectDetails={handleViewProjectDetails}
                    />
                  ))}
                </Box>
              </Box>
            ) : (
              <ProjectsTable
                projects={filteredProjects}
                onNavigateToExecution={handleCardNavigateToExecution}
              />
            )}
          </>
        )}

        <AddProjectModal
          isOpen={modalState.isOpen}
          onClose={handleCloseModal}
          onSubmit={handleAddProject}
          isSaving={isSavingProject}
          initialData={modalState.editingProjectData || undefined}
          initialErrors={modalState.initialErrors}
          isEditing={modalState.isEditing}
          existingProjectNames={projects.map(project => project.name)}
        />

        <RegenerateConfirmDialog
          isOpen={regenerateState.showConfirmation}
          project={getProjectById(regenerateState.projectId || '')}
          onConfirm={handleConfirmRegenerate}
          onCancel={handleCancelRegenerate}
        />
      </main>
    </>
  );
};
